import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useParams } from 'react-router-dom'

import { useSnackbar } from '../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../components/stories/molecules/snackbar/constants'

import MoveItemsModal from '../../../../components/move-items-modal'
import { planMenuApiErrorCodeOptions } from '../../constants'
import { getOrganizationName } from '../../../../react-query/authentication/query/use-me-query'
import { useGetPlanFolderDetailsQuery } from '../../../../react-query/plan/query/use-get-plan-folder-details-query'
import { useMovePlansAndFoldersMutation } from '../../../../react-query/plan/mutation/use-move-plans-and-folders-mutation'
import { recursiveListTypeOptions } from '../../../../components/recursive-folder-list/constants'
import { useGetPlanDetailsQuery } from '../../../../react-query/plan/query/use-get-plan-details-query'
import { getRootPlansWithFolders } from '../../../PlanSelection/actions'
import { useDispatch } from 'react-redux'
import { useGetProjectDetailsQuery } from '../../../../react-query/project/query/use-get-project-details-query'
import { usePlanMenuListViewControl } from '../../../../hooks/use-plan-menu-list-view-control'

const MovePlanModal = () => {
  const history = useHistory()
  const { formatMessage } = useIntl()
  const { addSnackbar } = useSnackbar()
  const dispatch = useDispatch()

  const { itemId, projectId, destinationFolderId } = useParams()
  const [moveItemsError, setMoveItemsError] = useState(null)
  const [destinationFolderHint, setDestinationFolderHint] = useState(null)

  const companyName = getOrganizationName()
  const { viewType } = usePlanMenuListViewControl()

  const { data: projectDetails, isLoading: projectDetailsLoading } =
    useGetProjectDetailsQuery({
      projectId
    })

  const planRootFolderId = projectDetails?.planRootFolderId

  const getPlanTree = () => dispatch(getRootPlansWithFolders(projectId))

  const showSnackbarSuccess = () => {
    addSnackbar({
      message: formatMessage({ id: 'planMenu_plan_moved' }),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  // Queries
  const { data: planDetails, isLoading: planDetailsLoading } =
    useGetPlanDetailsQuery(itemId, {
      enabled: !!itemId
    })

  const { data: parentFolderDetails, isLoading: parentFolderDetailsLoading } =
    useGetPlanFolderDetailsQuery(planDetails?.parentId, {
      enabled: !!planDetails?.parentId
    })

  // Mutations
  const { mutate: moveItems, isPending: moveItemsLoading } =
    useMovePlansAndFoldersMutation({
      onSuccess: handleMoveProjectSuccess,
      onError: handleMoveProjectError
    })

  // handlers
  const handleOnClose = () => {
    history.push(`/project/${projectId}?view=${viewType}`)
  }

  function handleMoveProjectError(error) {
    // user doesn't have permission to move items
    if (
      error &&
      error?.response?.data?.status ===
        planMenuApiErrorCodeOptions.NO_PERMISSION_TO_CREATE_PLAN_OR_FOLDER
    ) {
      return setMoveItemsError(
        formatMessage({
          id: 'projectMenu_no_permission_to_move'
        })
      )
    }

    if (
      error &&
      error?.response?.data?.status ===
        planMenuApiErrorCodeOptions.FOLDER_NO_LOGER_EXISTS
    ) {
      return setDestinationFolderHint(
        formatMessage({
          id: 'recursiveFolderList_the_selected_folder_no_longer_exists'
        })
      )
    }

    return setMoveItemsError(
      formatMessage({
        id: 'moveItems_move_error'
      })
    )
  }

  function handleMoveProjectSuccess() {
    getPlanTree()
    handleOnClose()
    showSnackbarSuccess()
  }

  const handleMoveItems = ({ parentFolderId }) => {
    setDestinationFolderHint(null)
    setMoveItemsError(null)
    moveItems({
      planIds: [itemId],
      destinationFolderId: parentFolderId,
      projectId
    })
  }

  const isLoading =
    planDetailsLoading || parentFolderDetailsLoading || projectDetailsLoading
  const modalTitle = formatMessage({ id: 'plans_movePlans' })

  const getOldFolderName = () => {
    if (
      parentFolderDetails?.name === companyName ||
      !parentFolderDetails?.name
    ) {
      return formatMessage({ id: 'main_folder' })
    }

    return parentFolderDetails?.name
  }

  const oldFolderName = getOldFolderName()

  useEffect(() => {
    if (destinationFolderId) {
      handleMoveItems({ parentFolderId: destinationFolderId })
    }
  }, [destinationFolderId])

  return (
    <MoveItemsModal
      handleClose={handleOnClose}
      handleMove={handleMoveItems}
      isLoading={isLoading}
      onMoveLoading={moveItemsLoading}
      onMoveError={moveItemsError}
      destinationFolderHint={destinationFolderHint}
      modalTitle={modalTitle}
      entityName={planDetails?.name}
      oldFolderName={oldFolderName}
      isMultipleItems={false}
      planRootFolderId={planRootFolderId}
      foldersListType={recursiveListTypeOptions.PLAN}
      destinationFolderId={destinationFolderId}
    />
  )
}

export default MovePlanModal
